/**
 * Album Cover Concept Generator — the Experimental Art tab's brain.
 *
 * Takes song context (title, artist, summary, lyrics) and returns THREE
 * distinct visual image-prompt concepts, each with LLM-picked modifiers
 * pulled from the cinematic taxonomy.
 *
 * System prompt lifted from FCAA — its whole value was the prompt itself.
 * Switched from Gemini to OpenAI (2026-07-20) after a Gemini service-account
 * outage broke this tab; uses the same openaiCompatFetch proxy + user's own
 * OpenAI key from localStorage that the rest of the app's OpenAI calls use.
 */

import { openaiCompatFetch } from '@/utils/openaiProxyFetch';
import { getOpenAIKey, getOpenAIModel } from './llmShared';
import { trackUsage } from '@/utils/costTracker';
import { CINEMATIC_MODIFIERS } from '@/data/cinematicModifiers';

export interface AlbumCoverInputs {
  title: string;
  /** Artist/band name. Pass "INVENT_A_BAND_NAME" to have the LLM invent one. */
  artist: string;
  summary: string;
  lyrics: string;
  /** e.g. '1:1', '5:7', '3:2', '16:9'. Declared explicitly so the model doesn't pick its own. */
  aspectRatio: string;
}

export interface AlbumCoverConcept {
  text: string;
  modifiers: Record<string, string[]>;
}

const MODIFIER_MAPPING = JSON.stringify(
  CINEMATIC_MODIFIERS.map(c => ({ category: c.name, options: c.options.map(o => o.label) })),
);

const SYSTEM_PROMPT = `You are an advanced album cover art concept generator. Your task is to analyze song lyrics, an artist name, a song title, and a summary to create THREE highly descriptive, distinct visual image prompts.

You must also select specific "Environmental Modifiers" from a provided list that match the vibe of your generated prompt.

Key directives:
1. **Aspect Ratio:** Every "text" field MUST open by stating the aspect ratio given in the input, exactly as given (e.g. "1:1 square album cover." or "5:7 vertical album cover."), before anything else. All three concepts share the same aspect ratio — vary the subject/environment/mood, not the frame shape.
2. **Diverse Interpretations:** Provide three DIFFERENT artistic directions — not three variations of the same idea. Different subject, different environment, different mood.
3. **Translate to Visuals:** Use professional camera and lighting terms (lens, aperture, angle, key/fill, film stock).
4. **CRITICAL TEXT RULE:** Do NOT write song lyrics as text in the image.
5. **ABSOLUTE DUAL-TEXT REQUIREMENT & PLACEMENT:** Each prompt MUST describe how to integrate BOTH the song title AND the artist name as raw text within the scene.
   - **COMPOSITION SAFETY:** Explicitly command that the text be "centered with generous padding" or "positioned well away from the image edges" to strictly prevent cropping. The artist name MUST be fully legible and not touching the bottom or sides.
   - If the Artist Name is provided as "INVENT A FITTING BAND NAME", creatively invent a band name that fits the genre/lyrics and use that invented name in the prompt description (e.g., "The band name 'VOID WALKER' appears on...").
6. **Modifier discipline:** Only pick modifiers strictly found in the provided list. Max 4 per category.

AVAILABLE MODIFIERS JSON:
${MODIFIER_MAPPING}

Format: Respond with a JSON object of the exact shape { "concepts": [ ... ] }, where "concepts" is an array of exactly 3 items. Each item must have:
- "text": The descriptive image prompt string.
- "modifiers": An object where keys are the exact category names from the list above, and values are arrays of selected option labels (max 4 per category).
Respond with ONLY that JSON object — no markdown fences, no commentary.`;

interface OpenAIChatResponse {
  choices?: { message?: { content?: string } }[];
  usage?: { prompt_tokens?: number; completion_tokens?: number };
  error?: { message?: string };
}

/**
 * Generate 3 distinct album cover concept prompts from song context.
 * Requires an OpenAI API key in localStorage (`openai_api_key`).
 */
export async function generateAlbumCoverConcepts(
  inputs: AlbumCoverInputs,
): Promise<AlbumCoverConcept[]> {
  const apiKey = getOpenAIKey();
  if (!apiKey) {
    throw new Error('OpenAI API key not found. Add your key in Settings.');
  }
  const model = getOpenAIModel();

  const artistInstruction =
    inputs.artist === 'INVENT_A_BAND_NAME' || !inputs.artist.trim()
      ? 'INVENT A FITTING BAND NAME'
      : `"${inputs.artist}"`;

  const prompt = [
    `Song Title: "${inputs.title || 'Untitled'}"`,
    `Artist: ${artistInstruction}`,
    `Aspect Ratio: ${inputs.aspectRatio || '1:1'}`,
    `Summary/Styles: ${inputs.summary || '(none provided)'}`,
    'Lyrics:',
    inputs.lyrics || '(none provided)',
  ].join('\n');

  const response = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      temperature: 0.95,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: prompt },
      ],
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as OpenAIChatResponse;
    throw new Error(`OpenAI API error: ${errorData?.error?.message || `HTTP ${response.status}`}`);
  }

  const responseData = await response.json() as OpenAIChatResponse;
  const text = responseData.choices?.[0]?.message?.content || '';
  const parsed = JSON.parse(text) as { concepts?: AlbumCoverConcept[] };

  trackUsage({
    provider: 'openai',
    model,
    feature: 'artwork',
    inputTokens: responseData.usage?.prompt_tokens || 0,
    outputTokens: responseData.usage?.completion_tokens || 0,
  });

  return Array.isArray(parsed.concepts) ? parsed.concepts.slice(0, 3) : [];
}
